import AssetAccordion from "../AssetAccordion/AssetAccordion";
import { useAtomValue, useSetAtom } from "jotai";
import {
  ProjectAssetType,
  fetchMembersSetter,
  HydratedGroupedAssets,
  membersAtomReadOnly,
  ProjectAssetsError,
  projectAssetsHydratedAtom_SUSPENDS,
  ProjectDetails,
} from "@zapier/canvas-state";
import { Suspense, ReactNode } from "react";
import {
  Alert,
  AlertDescription,
  Button,
  Icon,
  SkeletonText,
  Text,
} from "@zapier/design-system";
import styles from "./ProjectSidebar.module.css";
import { ProjectTitle } from "../ProjectTitle";

function ProjectIntro({
  buttonText,
  buttonHref,
  children,
}: {
  buttonText: string;
  buttonHref: string;
  children?: ReactNode;
}) {
  return (
    <div className={styles.wrapper}>
      <ProjectTitle />
      {children}
      <Button href={buttonHref} target="_blank">
        {buttonText}
      </Button>
      <div className={styles.emptyAssetRow}>
        <div>
          <Icon name="navFolder" size={20} />
        </div>
        <Text type="SmallPrint1">
          Organize the Zapier assets that are part of this Canvas.
        </Text>
      </div>
      <div className={styles.emptyAssetRow}>
        <div>
          <Icon name="personSettings" size={20} />
        </div>
        <Text type="SmallPrint1">
          Manage access across Zaps, Tables, Interfaces, and Chatbots.
        </Text>
      </div>
    </div>
  );
}

function NoProjectView() {
  return (
    <ProjectIntro buttonText="Go to Projects" buttonHref="/app/projects" />
  );
}

function EmptyProjectView({ project }: { project: ProjectDetails }) {
  return (
    <ProjectIntro
      buttonText="Add assets"
      buttonHref={`/app/projects/${project.id}`}
    >
      <Text type="Body1">
        This project doesn&apos;t have any assets other than this Canvas. Go add
        more stuff!
      </Text>
    </ProjectIntro>
  );
}

function SuspendedComponent() {
  const response = useAtomValue(projectAssetsHydratedAtom_SUSPENDS);
  const fetchAndSetMembers = useSetAtom(fetchMembersSetter);
  const members = useAtomValue(membersAtomReadOnly);
  const hasError = "error" in response;

  const hasNoProject = !hasError && response.assets === null;
  if (hasNoProject) {
    return <NoProjectView />;
  }

  if (hasError) {
    const errorResponse = response as ProjectAssetsError;

    const isNotAuthenticated = errorResponse.code === "not_authenticated";

    if (isNotAuthenticated) {
      return (
        <div className={styles.wrapper}>
          <Alert status="warning" title="Error getting project">
            <AlertDescription>Log in to see project details.</AlertDescription>
          </Alert>
        </div>
      );
    }

    if (errorResponse.permissionDenied) {
      const projectOwnerId = errorResponse.project?.owner_id;
      let projectOwner = projectOwnerId ? members[projectOwnerId] : undefined;
      if (projectOwnerId && !projectOwner) {
        fetchAndSetMembers([projectOwnerId]).then(() => {
          projectOwner = members[projectOwnerId];
        });
      }

      return (
        <div className={styles.wrapper}>
          <ProjectTitle showLink={false} />
          <Alert status="warning" title="No access permission">
            <AlertDescription>
              {`Please ask ${
                projectOwner ? projectOwner.full_name : "the project owner"
              } to share this project with you.`}
            </AlertDescription>
          </Alert>
        </div>
      );
    }

    return (
      <div className={styles.wrapper}>
        <ProjectTitle />
        <Alert status="warning" title="Unable to get assets">
          <AlertDescription>
            An error is preventing project assets from being displayed.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  const groupedAssets = response.assets as HydratedGroupedAssets;

  const projectIsEmpty = Object.values(groupedAssets).every(
    (assetType) => Array.isArray(assetType) && assetType.length === 0,
  );

  if (projectIsEmpty) {
    return <EmptyProjectView project={response.project!} />;
  }

  return (
    <div className={styles.wrapper}>
      <ProjectTitle />
      {Object.keys(groupedAssets).map((assetType) => {
        const type = assetType as ProjectAssetType;
        return (
          <AssetAccordion
            key={type}
            assetType={type}
            items={groupedAssets[type]}
          />
        );
      })}
    </div>
  );
}

function Loader() {
  return <SkeletonText lines={3} size="heading" />;
}

export default function ProjectSidebar() {
  return (
    <Suspense fallback={<Loader />}>
      <SuspendedComponent />
    </Suspense>
  );
}
